/* =========================================================================
 * region-products-reorder.js
 * -------------------------------------------------------------------------
 * Reordena/oculta cards de producto según la config por país/región
 * gestionada en Region Manager.
 *
 * - Lee metafields RAW desde `<script id="region-products-config">` y
 *   resuelve internamente: country → region → order/hide config.
 * - Detecta automáticamente containers con multiples <product-block>.
 * - Subset matching threshold: 30% (mínimo absoluto: 2 cards).
 * - Reactivo a mutaciones del DOM (paginate AJAX, Section Rendering API).
 * - Idempotente: containers procesados con data-region-applied.
 *
 * Debug: añade `?region_debug=1` a la URL para logs verbose en consola.
 *
 * Decisión de diseño (fix 2026-04-28):
 *   En collection-pages, el config a aplicar se elige EXPLÍCITAMENTE por
 *   `current_collection_id` (inyectado por region-products-data.liquid),
 *   NO por overlap de handles. Así Hombre y Mujer (que comparten productos)
 *   se reordenan independientes. La heurística de overlap solo se usa para
 *   containers fuera de collection-page (home carousels, etc.) donde no
 *   hay un collection.id "actual".
 * ========================================================================= */
(function () {
  'use strict';

  var SCRIPT_ID = 'region-products-config';
  var CARD_SELECTOR = 'product-block[data-product-handle]';
  var DEBUG = /[?&]region_debug=1\b/.test(window.location.search);
  var SUBSET_THRESHOLD = 0.3; // 30%
  var MIN_OVERLAP = 2;

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[region-reorder]');
    console.log.apply(console, args);
  }
  function warn() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[region-reorder]');
    console.warn.apply(console, args);
  }

  // Tarjeta cambiada por su par Wide (feat-46): se ordena y se oculta por el handle de la estándar,
  // que es el que está en el orden de Region Manager.
  function cardHandle(el) {
    if (!el || !el.getAttribute) return null;
    return el.getAttribute('data-region-handle') || el.getAttribute('data-product-handle');
  }

  function readRawConfig() {
    var el = document.getElementById(SCRIPT_ID);
    if (!el) { warn('script tag not found'); return null; }
    try {
      var data = JSON.parse(el.textContent || el.innerText || 'null');
      if (!data || typeof data !== 'object') { warn('parsed null'); return null; }
      return data;
    } catch (e) {
      warn('parse error', e);
      return null;
    }
  }

  function resolveRegionId(country, regions) {
    if (!country || !Array.isArray(regions)) return null;
    for (var i = 0; i < regions.length; i++) {
      var r = regions[i];
      if (r && Array.isArray(r.countries) && r.countries.indexOf(country) !== -1) {
        return r.id;
      }
    }
    return null;
  }

  function buildVisitorConfigs(rawConfig) {
    if (!rawConfig) return {};
    var country = rawConfig.country || '';
    var regions = rawConfig.regions || [];
    var ordersAll = rawConfig.orders || {};
    var managed = rawConfig.managed || [];

    var regionId = resolveRegionId(country, regions);
    log('country=' + country + ' regionId=' + regionId);

    var configs = {};
    for (var i = 0; i < managed.length; i++) {
      var handle = managed[i];
      var cfg = null;
      if (regionId && ordersAll[regionId]) cfg = ordersAll[regionId][handle];
      if (!cfg && ordersAll['*']) cfg = ordersAll['*'][handle];
      if (cfg && (Array.isArray(cfg.order) || Array.isArray(cfg.hide))) {
        configs[handle] = {
          order: Array.isArray(cfg.order) ? cfg.order : [],
          hide: Array.isArray(cfg.hide) ? cfg.hide : []
        };
      }
    }
    log('built configs', configs);
    return configs;
  }

  // Find the smallest ancestor that holds MULTIPLE product cards as
  // descendants in different direct children. In Symmetry-style themes
  // each card sits inside its own .card-wrapper, so we have to walk up
  // past the wrapper to reach the actual grid (e.g. .product-grid, ul).
  function findGridContainer(card) {
    var p = card.parentElement;
    while (p && p !== document.body) {
      var children = p.children;
      var sibCount = 0;
      for (var i = 0; i < children.length; i++) {
        var c = children[i];
        if ((c.matches && c.matches(CARD_SELECTOR)) ||
            (c.querySelector && c.querySelector(CARD_SELECTOR))) {
          sibCount++;
          if (sibCount >= 2) return p;
        }
      }
      p = p.parentElement;
    }
    return null;
  }

  function getCardsByContainer(root) {
    var cards = (root || document).querySelectorAll(CARD_SELECTOR);
    var map = new Map();
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var grid = findGridContainer(card);
      if (!grid) continue;
      if (!map.has(grid)) map.set(grid, []);
      map.get(grid).push(card);
    }
    return map;
  }

  function findBestCollectionMatch(handles, configs) {
    var bestKey = null;
    var bestOverlap = 0;
    var minOverlap = Math.max(MIN_OVERLAP, Math.ceil(handles.length * SUBSET_THRESHOLD));
    for (var key in configs) {
      if (!Object.prototype.hasOwnProperty.call(configs, key)) continue;
      var cfg = configs[key];
      if (!cfg) continue;
      var orderArr = Array.isArray(cfg.order) ? cfg.order : [];
      var hideArr = Array.isArray(cfg.hide) ? cfg.hide : [];
      if (!orderArr.length && !hideArr.length) continue;
      var orderSet = new Set(orderArr);
      var hideSet = new Set(hideArr);
      var overlap = 0;
      for (var i = 0; i < handles.length; i++) {
        if (orderSet.has(handles[i]) || hideSet.has(handles[i])) overlap++;
      }
      if (overlap >= minOverlap && overlap > bestOverlap) {
        bestOverlap = overlap;
        bestKey = key;
      }
    }
    return bestKey ? { handle: bestKey, overlap: bestOverlap, total: handles.length, minRequired: minOverlap } : null;
  }

  // Walk up from the card to find its "wrapper" — the direct child of the grid container.
  function getWrapperWithinGrid(card, grid) {
    var node = card;
    while (node.parentElement && node.parentElement !== grid) {
      node = node.parentElement;
    }
    return node.parentElement === grid ? node : null;
  }

  function applyReorder(grid, cards, cfg, matchInfo) {
    try {
      if (!cfg) { warn('applyReorder called with undefined cfg', matchInfo); return; }
      var orderArr = Array.isArray(cfg.order) ? cfg.order : [];
      var hideArr = Array.isArray(cfg.hide) ? cfg.hide : [];
      var orderIdx = new Map();
      for (var i = 0; i < orderArr.length; i++) orderIdx.set(orderArr[i], i);
      var hideSet = new Set(hideArr);

      var indexed = [];
      for (var k = 0; k < cards.length; k++) {
        var card = cards[k];
        if (!card || !card.getAttribute) continue;
        var h = cardHandle(card);
        var wrapper = getWrapperWithinGrid(card, grid);
        if (!wrapper) continue;
        var pos = orderIdx.has(h) ? orderIdx.get(h) : Number.MAX_SAFE_INTEGER;
        indexed.push({ card: card, wrapper: wrapper, pos: pos, originalIndex: k, handle: h });
      }
    } catch (err) {
      console.warn('[region-reorder] applyReorder error:', err && err.message, err && err.stack);
      return;
    }

    try {
      indexed.sort(function (a, b) {
        if (a.pos !== b.pos) return a.pos - b.pos;
        return a.originalIndex - b.originalIndex;
      });
      var sortedWrappers = indexed.map(function(it){ return it.wrapper; });

      var domOrderWrappers = sortedWrappers.slice().sort(function(a, b){
        var p = a.compareDocumentPosition(b);
        if (p & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (p & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });
      var placeholders = [];
      for (var pi = 0; pi < domOrderWrappers.length; pi++) {
        var w = domOrderWrappers[pi];
        if (!w.parentNode) continue;
        var ph = document.createComment('region-slot');
        w.parentNode.replaceChild(ph, w);
        placeholders.push(ph);
      }

      var reordered = 0;
      var hidden = 0;
      var len = Math.min(sortedWrappers.length, placeholders.length);
      for (var j = 0; j < len; j++) {
        var sw = sortedWrappers[j];
        var ph2 = placeholders[j];
        var pbInside = sw.querySelector ? sw.querySelector('product-block[data-product-handle]') : null;
        var swHandle = cardHandle(pbInside || sw);
        if (hideSet.has(swHandle)) {
          sw.style.display = 'none';
          sw.setAttribute('data-region-hidden', '1');
          hidden++;
        } else {
          if (sw.getAttribute('data-region-hidden') === '1') {
            sw.style.display = '';
            sw.removeAttribute('data-region-hidden');
          }
          reordered++;
        }
        if (ph2.parentNode) ph2.parentNode.replaceChild(sw, ph2);
      }
      for (var k2 = len; k2 < placeholders.length; k2++) {
        var leftover = placeholders[k2];
        if (leftover.parentNode) leftover.parentNode.removeChild(leftover);
      }

      grid.setAttribute('data-region-applied', matchInfo.handle);

      var carousel = grid.closest && grid.closest('carousel-slider');
      function resetCarousel() {
        try {
          if (carousel) {
            var inner = carousel.querySelector('.slider');
            if (inner) inner.scrollLeft = 0;
            if (typeof carousel.refresh === 'function') carousel.refresh();
          }
          var sc = grid;
          while (sc && sc !== document.body) {
            var cs = window.getComputedStyle(sc);
            var ovx = cs.overflowX === 'auto' || cs.overflowX === 'scroll' ||
                      cs.overflow === 'auto' || cs.overflow === 'scroll';
            if (ovx && sc.scrollWidth > sc.clientWidth) {
              sc.scrollLeft = 0;
              break;
            }
            sc = sc.parentElement;
          }
        } catch (eR) { warn('resetCarousel error', eR); }
      }
      resetCarousel();
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(function () { resetCarousel(); });
      }
      setTimeout(resetCarousel, 200);
      setTimeout(resetCarousel, 800);

      log('applied "' + matchInfo.handle + '" via ' + (matchInfo.source || 'overlap') + ' overlap=' + matchInfo.overlap + '/' + matchInfo.total + ' reordered=' + reordered + ' hidden=' + hidden, grid);
    } catch (err2) {
      console.warn('[region-reorder] applyReorder reorder error:', err2 && err2.message, err2 && err2.stack);
    }
  }

  /* Identifica el "container principal" de la collection-page: el grid con
     más cards. Los demás containers (cross-sells, recommended) seguirán
     usando la heurística de overlap como fallback. Esto evita aplicar la
     config explícita por accidente a un carousel "También te puede gustar"
     que vive en la misma página. */
  function findPrimaryContainer(byContainer) {
    var primary = null;
    var max = 0;
    byContainer.forEach(function (cards, parent) {
      if (Array.isArray(cards) && cards.length > max) {
        max = cards.length;
        primary = parent;
      }
    });
    return primary;
  }

  function reorderAll(visitorConfigs, root, currentCollectionId, currentParentId) {
    try {
      if (!visitorConfigs || !Object.keys(visitorConfigs).length) return;
      var byContainer = getCardsByContainer(root);
      log('scanning ' + byContainer.size + ' containers' +
          (currentCollectionId ? ' currentCollectionId=' + currentCollectionId : ''));

      var primary = null;
      var explicitCfg = null;
      var primaryNotManaged = false;
      var inheritedId = null; // si la hija hereda de su madre, id de la madre
      if (currentCollectionId) {
        primary = findPrimaryContainer(byContainer);
        if (visitorConfigs[currentCollectionId]) {
          explicitCfg = visitorConfigs[currentCollectionId];
          log('explicit override for collection ' + currentCollectionId + ' on primary container=' + (primary ? 'yes' : 'no'));
        } else if (currentParentId && currentParentId !== currentCollectionId && visitorConfigs[currentParentId]) {
          inheritedId = currentParentId;
          explicitCfg = visitorConfigs[currentParentId];
          log('inherited override: collection ' + currentCollectionId + ' inherits parent ' + currentParentId + ' on primary container=' + (primary ? 'yes' : 'no'));
        } else {
          primaryNotManaged = true;
          log('current collection ' + currentCollectionId + ' is NOT managed — primary grid will be left untouched (admin order respected)');
        }
      }

      byContainer.forEach(function (cards, parent) {
        try {
          if (!Array.isArray(cards) || cards.length < 2) return;
          var applied = parent.getAttribute('data-region-applied');
          if (applied) {
            var allSeen = cards.every(function (c) { return c && c.getAttribute && c.getAttribute('data-region-card-seen') === '1'; });
            if (allSeen) return;
          }

          var match;
          var dataAttrId = null;
          var dataHost = parent.closest && parent.closest('[data-region-collection-id]');
          if (dataHost) {
            dataAttrId = dataHost.getAttribute('data-region-collection-id');
          }

          if (parent === primary && explicitCfg) {
            var appliedId = inheritedId || currentCollectionId;
            match = { handle: appliedId, overlap: cards.length, total: cards.length, minRequired: 0, source: inheritedId ? 'inherited' : 'explicit' };
          } else if (parent === primary && primaryNotManaged) {
            log('skipping primary grid: collection ' + currentCollectionId + ' not managed');
            cards.forEach(function (c) { if (c && c.setAttribute) c.setAttribute('data-region-card-seen', '1'); });
            return;
          } else if (dataAttrId && visitorConfigs[dataAttrId]) {
            match = { handle: dataAttrId, overlap: cards.length, total: cards.length, minRequired: 0, source: 'data-attr' };
          } else if (dataAttrId) {
            // FIX-DATAATTR-AUTORIDAD-2026-08-19: el grid declara SU coleccion y esa coleccion
            // no esta gestionada en Region Manager -> se respeta el orden del admin y no se toca.
            // Antes se caia a la heuristica de overlap y se le aplicaba el orden de OTRA coleccion:
            // en la home, los grids de Lejan One FW26 mujer y hombre comparten los mismos 8
            // productos, empataban a 8 y ganaba el primero iterado (barefoot-para-mujer), asi que
            // la pestana Hombre mostraba el orden de Mujer. La heuristica sigue viva solo para
            // contenedores que NO declaran coleccion.
            log('skipping grid: collection ' + dataAttrId + ' is not managed — admin order respected');
            cards.forEach(function (c) { if (c && c.setAttribute) c.setAttribute('data-region-card-seen', '1'); });
            return;
          } else {
            var handles = [];
            for (var n = 0; n < cards.length; n++) {
              if (cards[n] && cards[n].getAttribute) handles.push(cardHandle(cards[n]));
            }
            match = findBestCollectionMatch(handles, visitorConfigs);
            if (!match) {
              log('no match for container (cards=' + cards.length + ')', handles);
              cards.forEach(function (c) { if (c && c.setAttribute) c.setAttribute('data-region-card-seen', '1'); });
              return;
            }
            match.source = 'overlap';
          }

          applyReorder(parent, cards, visitorConfigs[match.handle], match);
          cards.forEach(function (c) { if (c && c.setAttribute) c.setAttribute('data-region-card-seen', '1'); });
        } catch (innerErr) {
          console.warn('[region-reorder] container error:', innerErr && innerErr.message, innerErr && innerErr.stack);
        }
      });
    } catch (err) {
      console.warn('[region-reorder] reorderAll error:', err && err.message, err && err.stack);
    }
    document.documentElement.classList.remove('region-products-pending');
  }

  function debounce(fn, ms) {
    var t = null;
    return function () {
      var args = Array.prototype.slice.call(arguments);
      var ctx = this;
      if (t) clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function init() {
    var raw = readRawConfig();
    if (!raw) {
      document.documentElement.classList.remove('region-products-pending');
      return;
    }
    log('raw config', raw);

    var visitorConfigs = buildVisitorConfigs(raw);
    if (!Object.keys(visitorConfigs).length) {
      log('no overrides for this visitor — exiting');
      document.documentElement.classList.remove('region-products-pending');
      return;
    }

    var currentCollectionId = (raw && raw.current_collection_id) ? String(raw.current_collection_id) : '';
    var currentParentId = (raw && raw.current_parent_id) ? String(raw.current_parent_id) : '';
    var run = function () { reorderAll(visitorConfigs, undefined, currentCollectionId, currentParentId); };
    run();

    var observer = new MutationObserver(debounce(function (mutations) {
      var hasNewCards = false;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches(CARD_SELECTOR)) { hasNewCards = true; break; }
          if (n.querySelector && n.querySelector(CARD_SELECTOR)) { hasNewCards = true; break; }
        }
        if (hasNewCards) break;
      }
      if (hasNewCards) {
        log('mutations detected — re-running');
        run();
      }
    }, 50));
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---------------------------------------------------------------------
     API de solo lectura (2026-08-29). Aditiva: no cambia nada de lo de
     arriba. La expone para que otros bloques resuelvan la region del
     visitante con ESTA misma funcion en vez de duplicarla — hoy la usa
     snippets/block-recomendacion-talla.liquid para recomendar en el orden
     que el equipo tiene seteado por region. Si manana cambia la forma de
     resolver la region, cambia en un solo sitio.
     --------------------------------------------------------------------- */
  window.LejanRegionOrder = {
    resolveRegionId: resolveRegionId
  };
})();
